import { useEffect, useRef, useState } from 'react'
import type { UseInCallChatResult } from '../hooks/use-in-call-chat'

type InCallChatPanelProps = {
  chat: UseInCallChatResult
  resolveName: (senderId: string) => string
  /** Placeholder copy for the composer (defaults to a group call message). */
  placeholder?: string
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/**
 * Chat shown inside the call. Call-scoped: messages persist against the callId
 * as part of the call record (next to the transcript), NOT into a DM/group
 * thread. The chat state is owned by the overlay (so the live subscription
 * survives opening/closing the panel) and passed in via `chat`.
 */
export function InCallChatPanel({
  chat,
  resolveName,
  placeholder = 'Message the group…',
}: InCallChatPanelProps) {
  const { enabled, messages, loading, error, sending, send } = chat
  const [draft, setDraft] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const onSubmit = () => {
    if (!draft.trim()) return
    void send(draft)
    setDraft('')
  }

  if (!enabled) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-white/50">
        Chat isn’t available for this call.
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3"
        aria-label="In-call chat messages"
      >
        {loading && messages.length === 0 && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-white/10" />
            ))}
          </div>
        )}
        {!loading && error && messages.length === 0 && (
          <p className="px-1 py-2 text-sm text-amber-300" role="alert">
            {error}
          </p>
        )}
        {!loading && !error && messages.length === 0 && (
          <p className="px-1 py-2 text-sm text-white/50">
            No messages yet. This chat is saved with the call record.
          </p>
        )}
        {messages.map((m) => {
          if (m.messageType === 'SYSTEM') {
            return (
              <p
                key={m.id}
                className="mx-auto max-w-[90%] rounded-full bg-white/5 px-3 py-1 text-center text-[11px] text-white/50"
              >
                {m.text}
              </p>
            )
          }
          const mine = m.sender === 'me'
          return (
            <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
              {!mine && (
                <span className="px-1 text-[10px] font-semibold text-white/60">
                  {resolveName(m.senderId)}
                </span>
              )}
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${
                  mine
                    ? 'rounded-br-md bg-brand-600 text-white'
                    : 'rounded-bl-md bg-white/15 text-white'
                } ${m.id.startsWith('pending-') ? 'opacity-70' : ''}`}
              >
                {m.isDeleted ? (
                  <span className="italic text-white/50">Message deleted</span>
                ) : (
                  <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                    {m.text}
                  </span>
                )}
                <span className="mt-0.5 block text-[9px] text-white/50">
                  {formatTime(m.createdAt)}
                  {m.id.startsWith('pending-') ? ' · Sending…' : ''}
                </span>
              </div>
            </div>
          )
        })}
        <div ref={endRef} />
      </div>

      <div className="shrink-0 border-t border-white/10 p-2">
        {error && messages.length > 0 && (
          <p className="px-1 pb-1 text-[11px] text-amber-300" role="alert">
            {error}
          </p>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSubmit()
              }
            }}
            rows={1}
            placeholder={placeholder}
            aria-label={placeholder}
            className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/40"
          />
          <button
            type="button"
            onClick={onSubmit}
            disabled={!draft.trim() || sending}
            className="shrink-0 self-end rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
